import { copyToClipboard, stringifyUrl } from "@/utils";
import { Tag } from "@/types/Tag";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Trans, useLingui } from "@lingui/react/macro";
import { DialogProps } from "@radix-ui/react-dialog";
import { useI18n } from "@/contexts/i18nContext";
import { ROUTES } from "@/routes_new";
import { useState } from "react";
import { generatePath } from "react-router-dom";
import { Button } from "@components/ui/button";
import { Feature } from "@components/Feature";
import { ExternalLinkIcon, MessageSquareIcon } from "lucide-react";
import { postTrackingData } from "@/api/endpoints/track";
import { FEATURE } from "@/features";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from "@components/ui/item";
import { EVENT } from "@/tracking_events";
import { Field, FieldLabel } from "@components/ui/field";
import { Checkbox } from "@components/ui/checkbox";
import { toast } from "sonner";

export default function TagShareDialog({
  isFavoritePage,
  orgUuid,
  tag,
  ...rest
}: {
  isFavoritePage: boolean;
  tag: Tag | undefined;
  orgUuid: Uuid;
} & DialogProps) {
  const { language } = useI18n();
  const { t } = useLingui();

  // Local state
  const [shouldShowKpis, setshouldShowKpis] = useState(true);

  // Heleprs
  const tagName = isFavoritePage ? t`sparade omnämnanden` : tag?.name;

  const rssUrl =
    tag &&
    generatePath(ROUTES.TAG_EXPORT_RSS, {
      organization_uuid: orgUuid,
      slug: tag.slug,
      language,
    });

  const shareUrl =
    tag &&
    stringifyUrl({
      url: `${window.location.origin}${generatePath(ROUTES.TAG_PUBLIC, {
        language: language,
        id: tag.public_token,
      })}`,
      query: shouldShowKpis
        ? { kpis: shouldShowKpis, sentiment: shouldShowKpis }
        : {},
    });

  // Handlers
  async function handleCopyMentionsUrl() {
    postTrackingData(EVENT.SHARE_TAG_COPIED, {
      shareUrl: shareUrl,
    });

    try {
      if (!shareUrl) {
        throw new Error();
      }

      await copyToClipboard(shareUrl);

      toast.success(t`Länken kopierades`);
    } catch {
      toast.error(t`Länken kunde inte kopieras`);
    }
  }

  return (
    <Dialog {...rest}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Dela {tagName}</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>
              Dela alla omnämnanden för <q>{tagName}</q>
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Item variant="outline" size="sm">
            <ItemMedia variant="icon">
              <MessageSquareIcon />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>
                <Trans>Publik länk för omnämnanden</Trans>
              </ItemTitle>
              <Field orientation="horizontal">
                <Checkbox
                  id="show-kpis"
                  checked={shouldShowKpis}
                  onCheckedChange={(newChecked) => {
                    setshouldShowKpis(newChecked === true);
                  }}
                />

                <FieldLabel htmlFor="show-kpis">
                  <Trans>Visa PR-värde och räckvidd</Trans>
                </FieldLabel>
              </Field>
            </ItemContent>
            <ItemActions>
              {shareUrl && (
                <Button
                  render={
                    <a
                      href={shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  }
                  nativeButton={false}
                  size="icon-sm"
                  variant="ghost"
                  data-testid="share-mentions-link-preview"
                  onClick={() =>
                    postTrackingData(EVENT.SHARE_TAG_CLICKED, {
                      shareUrl,
                    })
                  }
                >
                  <ExternalLinkIcon />
                  <span className="sr-only">
                    <Trans>Öppna i en ny flik</Trans>
                  </span>
                </Button>
              )}

              <Button type="button" size="sm" onClick={handleCopyMentionsUrl}>
                <Trans>Kopiera</Trans>
              </Button>
            </ItemActions>
          </Item>

          <div className="flex flex-col gap-0 items-start">
            <Feature required={FEATURE.HAS_RSS_FEATURE}>
              {rssUrl && (
                <Button
                  render={
                    <a
                      href={rssUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  }
                  nativeButton={false}
                  size="sm"
                  variant="link"
                  onClick={() =>
                    postTrackingData(EVENT.SHARE_TAG_CLICKED_RSS, {
                      rssUrl,
                    })
                  }
                >
                  <ExternalLinkIcon />
                  <Trans>RSS feed för omnämnanden</Trans>
                </Button>
              )}
            </Feature>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
